import { ChangeEvent, memo, ReactNode, useCallback } from 'react';
import classes from 'classnames';
import styles from './Checkbox.module.scss';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import CheckboxEvent from '@/hooks/context/EventCollector/events/CheckboxEvent';

type Props = {
  children: ReactNode;
  className?: string;
  dataTest?: string;
  disabled?: boolean;
  error?: string;
  isTouched?: boolean;
  border?: boolean;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: boolean;
  pending?: boolean;
};

const CheckboxMemo = memo(function Checkbox({
  children,
  className,
  dataTest = 'checkbox',
  disabled,
  error,
  isTouched,
  border = false,
  name,
  onChange,
  value,
  pending,
  eventCollector,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleEventCollector = useCallback(
    (checked: boolean) => {
      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new CheckboxEvent({
          checkboxChecked: checked,
          description: eventCollector?.description ?? name,
        }),
      );
    },
    [eventCollector?.description, eventCollector?.dontTrack, name, track],
  );

  const showError = isTouched && error;

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
        handleEventCollector(e.target.checked);
      }
    },
    [onChange, handleEventCollector],
  );

  return (
    <div
      className={classes(className, styles.wrapper, border && styles.border)}
      data-test={`${dataTest}-wrapper`}
    >
      <label
        htmlFor={name}
        className={classes(
          styles.container,
          disabled && styles.disabled,
          pending && styles.pending,
        )}
      >
        <input
          type="checkbox"
          name={name}
          id={name}
          onChange={handleOnChange}
          value={String(value)}
          checked={value || false}
          data-test={dataTest}
          disabled={disabled || pending}
          className={classes(styles.invisible)}
        />
        <div className={styles.content} data-test={`${dataTest}-content`}>
          {children}
        </div>
        <span
          className={classes(styles.checkmark, showError && styles.error)}
        />
      </label>
      {showError && (
        <div
          className={styles['error-message']}
          data-test={`${dataTest}-error`}
        >
          {error}
        </div>
      )}
    </div>
  );
});

export default CheckboxMemo;
